/**
 * Use Thank You Pages Hook
 *
 * Custom hook for fetching and managing Thank You pages data.
 *
 * @since 1.0.0
 */

import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import { useEffect, useState } from 'react';

import { useNotification } from '../../contexts';
import type { APIResponse, ThankYouPage } from '../../types';
import type { PaginationInfo, UseThankYouPagesParams, UseThankYouPagesReturn } from './types';

/**
 * Custom hook for managing Thank You pages data
 *
 * @since 1.0.0
 *
 * @param params Hook parameters.
 *
 * @return Hook return value.
 */
export const useThankYouPages = ( params: UseThankYouPagesParams = {} ): UseThankYouPagesReturn => {
	const {
		search = '',
		postsPerPage = 20,
		page: initialPage = 1,
		orderBy = 'modified',
		order = 'DESC',
		status = 'any',
	} = params;

	const [ pages, setPages ] = useState< ThankYouPage[] >( [] );
	const [ loading, setLoading ] = useState< boolean >( true );
	const [ error, setError ] = useState< string | null >( null );
	const [ pagination, setPagination ] = useState< PaginationInfo | null >( null );
	const [ currentPage, setCurrentPage ] = useState< number >( initialPage );
	const [ currentPostsPerPage, setCurrentPostsPerPage ] = useState< number >( postsPerPage );
	const { addNotification } = useNotification();

	const fetchPages = async (): Promise< void > => {
		try {
			setLoading( true );
			setError( null );

			const queryParams = new URLSearchParams( {
				search,
				posts_per_page: currentPostsPerPage.toString(),
				page: currentPage.toString(),
				orderby: orderBy,
				order,
				status,
			} );

			const apiUrl = `/redfox-thank-you/v1/thank-you-pages?${ queryParams.toString() }`;

			const response = await apiFetch< APIResponse< ThankYouPage[] > >( {
				path: apiUrl,
			} );

			if ( response?.success && response.data ) {
				setPages( response.data );
				// Set pagination info
				if ( response.pagination ) {
					setPagination( {
						currentPage: response.pagination.current_page,
						totalPages: response.pagination.total_pages,
						totalItems: response.pagination.total_items,
						itemsPerPage: response.pagination.items_per_page,
						hasNextPage: response.pagination.has_next_page,
						hasPrevPage: response.pagination.has_prev_page,
					} );
				} else {
					setPagination( null );
				}
			} else {
				setPages( [] );
				setPagination( null );
			}
		} catch ( err ) {
		// eslint-disable-next-line no-console
			console.error( 'Failed to fetch Thank You pages:', err );
			const errorMessage = err instanceof Error ? err.message : __( 'Failed to fetch Thank You pages', 'redfox-thank-you' );
			setError( errorMessage );
			addNotification( 'error', errorMessage );
			setPages( [] );
			setPagination( null );
		} finally {
			setLoading( false );
		}
	};

	const deletePage = async ( pageId: number ): Promise< boolean > => {
		try {
			await apiFetch( {
				path: `/redfox-thank-you/v1/thank-you-pages/${ pageId }`,
				method: 'DELETE',
			} );

			// Remove the deleted page from local state
			setPages( ( prevPages ) => prevPages.filter( ( page ) => page.id !== pageId ) );
			addNotification( 'success', __( 'Page deleted successfully!', 'redfox-thank-you' ) );
			return true;
		} catch ( err ) {
		// eslint-disable-next-line no-console
			console.error( 'Failed to delete Thank You page:', err );
			const errorMessage = err instanceof Error ? err.message : __( 'Failed to delete Thank You page', 'redfox-thank-you' );
			setError( errorMessage );
			addNotification( 'error', errorMessage );
			return false;
		}
	};

	/**
	 * Navigate to a specific page
	 *
	 * @since 1.0.0
	 *
	 * @param page Page number to navigate to.
	 */
	const goToPage = ( page: number ): void => {
		if ( page >= 1 && ( ! pagination || page <= pagination.totalPages ) ) {
			setCurrentPage( page );
		}
	};

	/**
	 * Change items per page
	 *
	 * @since 1.0.0
	 *
	 * @param itemsPerPage Number of items per page.
	 */
	const changeItemsPerPage = ( itemsPerPage: number ): void => {
		setCurrentPostsPerPage( itemsPerPage );
		setCurrentPage( 1 ); // Reset to first page when changing items per page
	};

	useEffect( () => {
		fetchPages();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ search, currentPostsPerPage, currentPage, orderBy, order, status, addNotification ] );

	return {
		pages,
		loading,
		error,
		pagination,
		refetch: fetchPages,
		deletePage,
		goToPage,
		changeItemsPerPage,
	};
};

